import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import {
  CloudRain, Sun, CloudSnow, Wind, Droplets, Cloud, CloudLightning,
  Thermometer, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";
import { SpeakButton } from "./SpeakButton";
import { DISPLAY } from "@/styles/editorial";

const B = {
  border: "2px solid #29294d",
  shadow: "none",
  shadowSm: "none",
  font: "'Albert Sans', 'Inter', sans-serif",
  mono: "'IBM Plex Mono', monospace",
};

const WMO_CODES = {
  0: { label: "Despejado", icon: Sun }, 1: { label: "Poco nuboso", icon: Sun },
  2: { label: "Parcialmente nublado", icon: Cloud }, 3: { label: "Nublado", icon: Cloud },
  45: { label: "Niebla", icon: Cloud }, 48: { label: "Niebla helada", icon: CloudSnow },
  51: { label: "Llovizna ligera", icon: Droplets }, 53: { label: "Llovizna", icon: Droplets },
  55: { label: "Llovizna intensa", icon: CloudRain }, 61: { label: "Lluvia ligera", icon: CloudRain },
  63: { label: "Lluvia moderada", icon: CloudRain }, 65: { label: "Lluvia intensa", icon: CloudRain },
  71: { label: "Nieve ligera", icon: CloudSnow }, 73: { label: "Nieve moderada", icon: CloudSnow },
  75: { label: "Nieve intensa", icon: CloudSnow }, 80: { label: "Chubascos ligeros", icon: CloudRain },
  81: { label: "Chubascos moderados", icon: CloudRain }, 82: { label: "Chubascos fuertes", icon: CloudRain },
  95: { label: "Tormenta", icon: CloudLightning }, 96: { label: "Tormenta con granizo", icon: CloudLightning },
  99: { label: "Tormenta fuerte", icon: CloudLightning },
};

const AEMET_SKY_ICONS = {
  "Despejado": Sun, "Poco nuboso": Sun, "Nubes altas": Sun,
  "Intervalos nubosos": Cloud, "Nuboso": Cloud, "Muy nuboso": Cloud, "Cubierto": Cloud,
};

const LEVEL_CONFIG = {
  rojo: { color: "#f64e0e", bg: "#f64e0e15", label: "ROJO" },
  naranja: { color: "#fa9000", bg: "#fa900015", label: "NARANJA" },
  amarillo: { color: "#fa9000", bg: "#fa900010", label: "AMARILLO" },
  verde: { color: "#81801a", bg: "#81801a15", label: "VERDE" },
};

const WEATHER_BACKGROUNDS = {
  night_clear: "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=1200&q=80&fit=crop",
  night_cloudy: "https://images.unsplash.com/photo-1534088568595-a066f410bcda?w=1200&q=80&fit=crop",
  night_rain: "https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1200&q=80&fit=crop",
  night_snow: "https://images.unsplash.com/photo-1478265409131-1f65c88f965c?w=1200&q=80&fit=crop",
  day_clear: "https://images.unsplash.com/photo-1601297183305-6df142704ea2?w=1200&q=80&fit=crop",
  day_cloudy: "https://images.unsplash.com/photo-1536514498073-50e69d39c6cf?w=1200&q=80&fit=crop",
  day_rain: "https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=1200&q=80&fit=crop",
  day_snow: "https://images.unsplash.com/photo-1511131341194-24e2eeeebb09?w=1200&q=80&fit=crop",
};

function getWeatherBackground(weatherCode, isDay) {
  const prefix = isDay ? "day" : "night";
  if (weatherCode >= 71 && weatherCode <= 75) return WEATHER_BACKGROUNDS[`${prefix}_snow`];
  if ((weatherCode >= 51 && weatherCode <= 65) || (weatherCode >= 80 && weatherCode <= 99)) return WEATHER_BACKGROUNDS[`${prefix}_rain`];
  if ((weatherCode >= 2 && weatherCode <= 3) || (weatherCode >= 45 && weatherCode <= 48)) return WEATHER_BACKGROUNDS[`${prefix}_cloudy`];
  return WEATHER_BACKGROUNDS[`${prefix}_clear`];
}

function getWeatherIcon(code) { return WMO_CODES[code] || { label: "Desconocido", icon: Cloud }; }

function getAemetIcon(cielo) {
  if (!cielo) return Cloud;
  for (const [key, Icon] of Object.entries(AEMET_SKY_ICONS)) {
    if (cielo.toLowerCase().includes(key.toLowerCase())) return Icon;
  }
  if (cielo.toLowerCase().includes("lluvia")) return CloudRain;
  if (cielo.toLowerCase().includes("nieve")) return CloudSnow;
  if (cielo.toLowerCase().includes("tormenta")) return CloudLightning;
  return Cloud;
}

export default function WeatherWidget({ compact = false, municipio = "madrid", userLat = null, userLng = null, userName = null }) {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlerts, setShowAlerts] = useState(false);
  const [visibleAlerts, setVisibleAlerts] = useState(5);
  const [bgLoaded, setBgLoaded] = useState(false);

  const current = weather?.current;
  const isDay = current?.is_day === 1;
  const weatherCode = current?.weather_code ?? 0;
  const backgroundUrl = getWeatherBackground(weatherCode, isDay);

  useEffect(() => {
    const lat = userLat || 40.42;
    const lng = userLng || -3.70;
    axios.get(`${API}/weather?lat=${lat}&lng=${lng}`).then((r) => setWeather(r.data)).catch(() => {});
    axios.get(`${API}/aemet/forecast/${municipio}`).then((r) => { if (!r.data.error) setForecast(r.data); }).catch(() => {});
    axios.get(`${API}/aemet/alerts`).then((r) => { if (r.data.alerts) setAlerts(r.data.alerts); }).catch(() => {}).finally(() => setLoading(false));
  }, [municipio, userLat, userLng]);

  useEffect(() => {
    setBgLoaded(false);
    const img = new Image();
    img.src = backgroundUrl;
    img.onload = () => setBgLoaded(true);
  }, [backgroundUrl]);

  if (loading) {
    return <div className="p-4 bg-white animate-pulse" style={{ border: B.border }}><div className="h-20" /></div>;
  }

  const temp = current ? Math.round(current.temperature_2m) : null;
  const humidity = current?.relative_humidity_2m;
  const windSpeed = current ? Math.round(current.wind_speed_10m) : null;
  const info = getWeatherIcon(weatherCode);
  const WeatherIcon = info.icon;
  const highAlerts = alerts.filter((a) => a.level === "rojo" || a.level === "naranja");
  const allAlerts = alerts.filter((a) => a.level !== "verde");
  const hasHighAlerts = highAlerts.length > 0;

  // Color based on time of day (fallback when no bg image)
  const bgColor = isDay ? "#686af3" : "#29294d";
  const accentColor = isDay ? "#fa9000" : "#a88efb";

  if (compact) {
    return (
      <div data-testid="weather-widget-compact" className="space-y-2">
        <div className="p-4 flex items-center gap-3 relative overflow-hidden"
          style={{ border: B.border, boxShadow: B.shadowSm, minHeight: "72px" }}
        >
          {/* Background image */}
          <div className="absolute inset-0" style={{
            backgroundImage: bgLoaded ? `url(${backgroundUrl})` : "none",
            backgroundColor: bgColor,
            backgroundSize: "cover", backgroundPosition: "center",
            transition: "opacity 0.5s ease",
          }} />
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.45)" }} />

          <div className="relative z-10 flex items-center gap-3 w-full">
            {temp !== null && <WeatherIcon className="w-8 h-8" style={{ color: accentColor }} />}
            <div className="flex-1">
              {temp !== null && <p className="text-lg font-bold text-white" style={{ fontFamily: B.mono }}>{temp}°C</p>}
              <p className="text-xs text-white/70" style={{ fontFamily: B.mono }}>{info.label}</p>
            </div>
            {forecast?.days?.[0] && (
              <div className="text-right">
                <p className="text-xs text-white/80" style={{ fontFamily: B.mono }}>{forecast.days[0].temp_min}°/{forecast.days[0].temp_max}°</p>
                <p className="text-[10px] text-white/50" style={{ fontFamily: B.mono }}>AEMET</p>
              </div>
            )}
          </div>
        </div>
        {hasHighAlerts && (
          <div className="px-3 py-2 flex items-center gap-2 cursor-pointer" style={{ background: "#f64e0e15", border: "2px solid #f64e0e" }}
            onClick={() => setShowAlerts(!showAlerts)} data-testid="compact-alert-banner"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: "#f64e0e" }} />
            <span className="text-xs font-bold flex-1 truncate" style={{ fontFamily: B.mono, color: "#f64e0e" }}>{highAlerts[0].event}</span>
            <span className="text-[10px] font-bold px-1.5 py-0.5 text-white" style={{ background: "#f64e0e" }}>{highAlerts.length}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="weather-widget" className="space-y-3">
      <div className="p-6 relative overflow-hidden" style={{ border: B.border, boxShadow: B.shadow, minHeight: "280px" }}>
        {/* Background image */}
        <div className="absolute inset-0" style={{
          backgroundImage: bgLoaded ? `url(${backgroundUrl})` : "none",
          backgroundColor: bgColor,
          backgroundSize: "cover", backgroundPosition: "center",
          transition: "opacity 0.5s ease",
        }} />
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.4)" }} />
        {hasHighAlerts && (
          <div className="absolute top-2 right-2 z-20 text-[10px] font-bold px-2.5 py-1 text-white" style={{ fontFamily: B.mono, background: "#f64e0e", borderRadius: 3 }}>
            {highAlerts.length} ALERTA{highAlerts.length > 1 ? "S" : ""}
          </div>
        )}

        <div className="relative z-10">
        <div className="flex items-start justify-between mb-4 pt-2">
          <div>
            <p className="text-xs uppercase tracking-widest text-white/60" style={{ fontFamily: B.mono }}>
              {isDay ? "DÍA" : "NOCHE"} // {userName || forecast?.nombre || "Madrid"}
            </p>
            {temp !== null && (
              <p className="text-5xl font-bold mt-1 text-white" style={{ fontFamily: B.font }}>
                {temp}<span className="text-xl">°C</span>
              </p>
            )}
          </div>
          {temp !== null && <WeatherIcon className="w-14 h-14 mt-4" style={{ color: accentColor }} />}
        </div>

        <p className="text-sm mb-4 text-white/90" style={{ fontFamily: "'Inter', sans-serif" }}>
          {forecast?.days?.[0]?.cielo || info.label}
        </p>

        <div className="flex items-center gap-6">
          {humidity != null && (
            <div className="flex items-center gap-1.5">
              <Droplets className="w-4 h-4 text-white/50" />
              <span className="text-xs text-white/70" style={{ fontFamily: B.mono }}>{humidity}%</span>
            </div>
          )}
          {windSpeed != null && (
            <div className="flex items-center gap-1.5">
              <Wind className="w-4 h-4 text-white/50" />
              <span className="text-xs text-white/70" style={{ fontFamily: B.mono }}>{windSpeed} km/h</span>
            </div>
          )}
          {forecast?.days?.[0] && (
            <div className="flex items-center gap-1.5">
              <Thermometer className="w-4 h-4 text-white/50" />
              <span className="text-xs text-white/70" style={{ fontFamily: B.mono }}>{forecast.days[0].temp_min}°/{forecast.days[0].temp_max}°</span>
            </div>
          )}
        </div>

        {forecast?.days && forecast.days.length > 1 && (
          <div className="mt-4 pt-4 grid grid-cols-3 gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.15)" }}>
            {forecast.days.map((day, i) => {
              const DayIcon = getAemetIcon(day.cielo);
              const dayLabel = i === 0 ? "HOY" : i === 1 ? "MAÑANA" : new Date(day.fecha).toLocaleDateString("es-ES", { weekday: "short" }).toUpperCase();
              return (
                <div key={day.fecha} className="text-center">
                  <p className="text-[10px] uppercase tracking-widest text-white/50" style={{ fontFamily: B.mono }}>{dayLabel}</p>
                  <DayIcon className="w-5 h-5 mx-auto my-1" style={{ color: accentColor }} />
                  <p className="text-xs text-white" style={{ fontFamily: B.mono }}>{day.temp_min}°/{day.temp_max}°</p>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-3 flex items-center justify-end">
          <span className="text-[9px] text-white/30" style={{ fontFamily: B.mono }}>AEMET + OPEN-METEO</span>
        </div>
        </div>
      </div>

      {allAlerts.length > 0 && (
        <div className="bg-white overflow-hidden" style={{ border: B.border }} data-testid="aemet-alerts-panel">
          <button
            onClick={() => { setShowAlerts(!showAlerts); setVisibleAlerts(5); }}
            className="w-full px-6 py-5 flex items-center justify-between transition-colors hover:bg-[#f0eee9]"
            data-testid="toggle-alerts-btn"
          >
            <div className="flex items-center gap-3 flex-wrap text-left">
              <span className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: hasHighAlerts ? "#f64e0e" : "#fa9000" }}>
                <AlertTriangle className="w-5 h-5 text-white" />
              </span>
              <span className="text-xl md:text-2xl tracking-tight" style={{ fontFamily: DISPLAY, color: "#29294d" }}>Avisos AEMET</span>
              <span className="text-sm font-bold px-2.5 py-1 text-white" style={{ fontFamily: B.mono, background: hasHighAlerts ? "#f64e0e" : "#fa9000" }}>
                {allAlerts.length}
              </span>
              <SpeakButton
                text={`Avisos AEMET. ${allAlerts.length} avisos activos. ${allAlerts.slice(0, 5).map(a => `${a.event} en ${a.area}. Nivel ${a.level}.`).join(' ')}`}
                label="Escuchar avisos"
              />
            </div>
            {showAlerts ? <ChevronUp className="w-6 h-6" style={{ color: "#29294d" }} /> : <ChevronDown className="w-6 h-6" style={{ color: "#29294d" }} />}
          </button>

          {showAlerts && (
            <div className="px-6 pb-6" style={{ borderTop: "2px solid #29294d" }}>
              <div className="pt-4 space-y-3">
                {allAlerts.slice(0, visibleAlerts).map((alert, i) => {
                  const level = LEVEL_CONFIG[alert.level] || LEVEL_CONFIG.verde;
                  return (
                    <div key={i} className="p-4 flex items-start gap-4" style={{ backgroundColor: level.bg, border: `2px solid ${level.color}`, borderLeft: `8px solid ${level.color}` }} data-testid={`alert-item-${i}`}>
                      <AlertTriangle className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: level.color }} />
                      <div className="flex-1 min-w-0">
                        <span className="text-[11px] font-bold px-2 py-0.5 text-white inline-block mb-1.5 tracking-widest" style={{ fontFamily: B.mono, background: level.color }}>{level.label}</span>
                        <p className="text-base md:text-lg font-bold leading-snug" style={{ color: "#29294d" }}>{alert.event}</p>
                        <p className="text-sm mt-1" style={{ fontFamily: B.mono, color: "#666" }}>{alert.area}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              {allAlerts.length > visibleAlerts && (
                <button
                  onClick={() => setVisibleAlerts((v) => v + 5)}
                  className="w-full mt-3 py-3.5 text-sm font-bold uppercase tracking-[0.15em] transition-colors hover:bg-[#29294d] hover:text-white"
                  style={{ fontFamily: B.mono, color: "#29294d", border: "2px solid #29294d", background: "transparent" }}
                  data-testid="show-more-alerts-btn"
                >
                  Ver más alertas ({allAlerts.length - visibleAlerts} restantes)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
